/**
 * cierreAuditMetrics.ts — Métricas contables compartidas del ticket de cierre.
 *
 * Centraliza el cálculo de caja física, cuadrante neto y auditoría operativa
 * para mantener consistencia entre preview web, ESC/POS e histórico.
 */

import type { CierreCaja, MovimientoCaja, Venta } from "../types/caja.types";
import { splitEgresosCajaFisica } from "./splitEgresosCajaFisica";
import {
  calcCuadranteNeto,
  calcEsperadoGaveta,
  calcRendimientoOperativo,
} from "./cierreCuadranteHelper";
import {
  sumNetoIngresoPorMetodo,
  sumPropinaPorMetodo,
  sumSubtotalPorMetodo,
} from "./propinaAllocation";
import { isMovimientoAbonoCredito } from "./movimientoAbonoCredito";

export interface CierreAuditMetrics {
  movimientosActivos: MovimientoCaja[];
  movCreditoAbonos: MovimientoCaja[];
  movOtros: MovimientoCaja[];
  totalVentasEfectivoNetas: number;
  totalVentasDigitalNetas: number;
  propinasEfectivo: number;
  totalIngresosManualesEfectivo: number;
  totalIngresosManualesDigital: number;
  totalAbonosCreditoEfectivo: number;
  totalAbonosCreditoDigital: number;
  totalDevolucionesEfectivo: number;
  totalDevoluciones: number;
  totalSalidasAuditoria: number;
  retirosTop: number;
  retirosOtros: number;
  totalRetirosSeguridad: number;
  totalGastos: number;
  esperadoGaveta: number;
  cuadranteNeto: ReturnType<typeof calcCuadranteNeto>;
  efectivoEsperadoSinEntregar: number;
  cajaHoyEfectivo: number;
  cajaHoyDigital: number;
  totalRecaudado: number;
  ventasEfectivoRecaudo: number;
  ventasDigitalRecaudo: number;
  recaudoEfectivo: number;
  recaudoDigital: number;
  totalAnulaciones: number;
  rendimiento: ReturnType<typeof calcRendimientoOperativo>;
}

export function buildCierreAuditMetrics(
  cierre: Pick<
    CierreCaja,
    "montoInicial" | "montoCierre" | "movimientosSnapshot" | "baseSiguiente"
  >,
  ventas: Venta[],
): CierreAuditMetrics {
  const ventasActivas = ventas.filter((v) => v.estado !== "anulada");
  const subtotalPorMetodo = sumSubtotalPorMetodo(ventasActivas);
  const netoIngresoPorMetodo = sumNetoIngresoPorMetodo(ventasActivas);
  const propinaPorMetodo = sumPropinaPorMetodo(ventasActivas);

  const movimientosActivos = (cierre.movimientosSnapshot ?? []).filter(
    (m) => m.estado !== "anulado",
  );
  const movCreditoAbonos = movimientosActivos.filter(isMovimientoAbonoCredito);
  const movOtros = movimientosActivos.filter(
    (m) => !isMovimientoAbonoCredito(m),
  );
  const egresos = splitEgresosCajaFisica(movimientosActivos);

  const totalIngresosManualesEfectivo = movOtros
    .filter(
      (m) =>
        m.tipo === "ingreso" && (m.metodoPago === "efectivo" || !m.metodoPago),
    )
    .reduce((sum, m) => sum + m.monto, 0);

  const totalIngresosManualesDigital = movOtros
    .filter(
      (m) =>
        m.tipo === "ingreso" && m.metodoPago && m.metodoPago !== "efectivo",
    )
    .reduce((sum, m) => sum + m.monto, 0);

  const totalAbonosCreditoEfectivo = movCreditoAbonos
    .filter((m) => m.metodoPago === "efectivo" || !m.metodoPago)
    .reduce((sum, m) => sum + m.monto, 0);

  const totalAbonosCreditoDigital = movCreditoAbonos
    .filter((m) => m.metodoPago && m.metodoPago !== "efectivo")
    .reduce((sum, m) => sum + m.monto, 0);

  const totalDevolucionesEfectivo = movimientosActivos
    .filter(
      (m) =>
        m.tipo === "devolucion" &&
        (m.metodoPago === "efectivo" || !m.metodoPago),
    )
    .reduce((sum, m) => sum + m.monto, 0);

  const totalDevoluciones = movimientosActivos
    .filter((m) => m.tipo === "devolucion")
    .reduce((sum, m) => sum + m.monto, 0);

  const totalSalidasAuditoria = movimientosActivos
    .filter(
      (m) =>
        m.tipo === "retiro" ||
        m.tipo === "gasto" ||
        m.tipo === "devolucion" ||
        m.tipo === "anulacion",
    )
    .reduce((sum, m) => sum + m.monto, 0);

  const totalAnulaciones = movimientosActivos
    .filter((m) => m.tipo === "anulacion")
    .reduce((sum, m) => sum + m.monto, 0);

  const totalVentasEfectivoNetas = subtotalPorMetodo.efectivo;
  const totalVentasDigitalNetas =
    subtotalPorMetodo.nequi +
    subtotalPorMetodo.daviplata +
    subtotalPorMetodo.tarjeta;

  const { montoEsperado: esperadoGaveta } = calcEsperadoGaveta({
    montoInicial: cierre.montoInicial,
    /**
     * La propina se suma en su propia fila. Usar el subtotal evita contarla
     * dos veces cuando el cobro por método ya la incluye.
     */
    ventasEfectivo: subtotalPorMetodo.efectivo,
    ingresosEfectivo: totalIngresosManualesEfectivo,
    abonosCreditoEfectivo: totalAbonosCreditoEfectivo,
    propinasEfectivo: propinaPorMetodo.efectivo,
    retiros: egresos.retirosTop + egresos.retirosOtros,
    gastos: egresos.gastos,
    devolucionesEfectivo: totalDevolucionesEfectivo,
  });

  const cuadranteNeto = calcCuadranteNeto({
    totalContado: cierre.montoCierre || 0,
    baseReservada:
      typeof cierre.baseSiguiente === "number"
        ? cierre.baseSiguiente
        : cierre.montoInicial,
    propinasEfectivo: propinaPorMetodo.efectivo,
  });

  const efectivoEsperadoSinEntregar = Math.max(
    0,
    esperadoGaveta - cierre.montoInicial - propinaPorMetodo.efectivo,
  );

  const ventasEfectivoRecaudo =
    totalVentasEfectivoNetas + propinaPorMetodo.efectivo;
  const ventasDigitalNetoIngreso =
    netoIngresoPorMetodo.nequi +
    netoIngresoPorMetodo.daviplata +
    netoIngresoPorMetodo.tarjeta;
  const ventasDigitalRecaudo = ventasDigitalNetoIngreso;

  const totalRetiros = egresos.retirosTop + egresos.retirosOtros;
  const recaudoEfectivo =
    ventasEfectivoRecaudo +
    totalIngresosManualesEfectivo +
    totalRetiros +
    totalAbonosCreditoEfectivo;
  const recaudoDigital =
    ventasDigitalRecaudo +
    totalIngresosManualesDigital +
    totalAbonosCreditoDigital;
  const totalRecaudado = recaudoEfectivo + recaudoDigital;

  const faltanteArqueo = (cierre.montoCierre ?? 0) - esperadoGaveta;

  const cajaHoyEfectivo =
    totalVentasEfectivoNetas +
    totalIngresosManualesEfectivo +
    totalAbonosCreditoEfectivo;
  const cajaHoyDigital =
    totalVentasDigitalNetas +
    totalIngresosManualesDigital +
    totalAbonosCreditoDigital;

  const rendimiento = calcRendimientoOperativo({
    totalRecaudado,
    totalGastos: egresos.gastos,
    totalDevoluciones,
    totalContadoNeto: cuadranteNeto.efectivoNetoEntregar,
    totalDigital: recaudoDigital,
    faltanteArqueo,
  });

  return {
    movimientosActivos,
    movCreditoAbonos,
    movOtros,
    totalVentasEfectivoNetas,
    totalVentasDigitalNetas,
    propinasEfectivo: propinaPorMetodo.efectivo,
    totalIngresosManualesEfectivo,
    totalIngresosManualesDigital,
    totalAbonosCreditoEfectivo,
    totalAbonosCreditoDigital,
    totalDevolucionesEfectivo,
    totalDevoluciones,
    totalSalidasAuditoria,
    retirosTop: egresos.retirosTop,
    retirosOtros: egresos.retirosOtros,
    totalRetirosSeguridad: egresos.retirosTop,
    totalGastos: egresos.gastos,
    esperadoGaveta,
    cuadranteNeto,
    efectivoEsperadoSinEntregar,
    cajaHoyEfectivo,
    cajaHoyDigital,
    totalRecaudado,
    ventasEfectivoRecaudo,
    ventasDigitalRecaudo,
    recaudoEfectivo,
    recaudoDigital,
    totalAnulaciones,
    rendimiento,
  };
}
